import React, { useState, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useError } from "../../../contexts/ErrorContext";
import { useAuth } from "../../../contexts/AuthContext";
import { useStudents, StudentRollScholarUpdateDto } from "../../../hooks/useStudents";
import { useSessionClass } from "../../../contexts/SessionClassContext";
import RollNumberModal from "../../../components/Modals/RollNumberModal";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import CommonModal from "../../../components/common/CommonModal";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const formatRollNumber = (roll: string | number | null | undefined) => {
  if (roll === null || roll === undefined || roll === "") return "-";
  const str = String(roll).trim();
  if (/^\d+$/.test(str)) {
    return str.padStart(2, '0');
  }
  return str;
};

const RollNumberCard: React.FC<{
  studentName: string;
  rollNumber?: string | number | null;
  isPrint?: boolean;
}> = ({ studentName, rollNumber, isPrint }) => {
  const formattedRoll = formatRollNumber(rollNumber);

  return (
    <Box
      className={isPrint ? "roll-card-print" : undefined}
      sx={{
        width: "100%",
        minWidth: 0,
        overflow: "hidden",
        height: "90px",
        backgroundColor: "#ffffff",
        border: "1px solid #E2E8F0",
        borderRadius: "10px",
        boxShadow: isPrint ? "none" : "0 2px 8px rgba(0,0,0,0.08)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
        boxSizing: "border-box",
        p: "12px 8px",
        fontFamily: "'Inter', sans-serif",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        cursor: "default",
        ...(!isPrint && {
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
          }
        })
      }}
    >
      {/* Roll Number */}
      <Box
        sx={{
          fontSize: "clamp(11px, 1.5vw, 20px)",
          fontWeight: "bold",
          color: "#3092e3",
          textAlign: "center",
          lineHeight: 1.1,
          width: "100%",
          whiteSpace: "nowrap",
          overflow: "hidden",
          display: "block",
        }}
      >
        {formattedRoll}
      </Box>

      {/* Divider */}
      <Box
        sx={{
          width: "80%",
          height: "1px",
          bgcolor: "#E2E8F0",
          my: "4px",
        }}
      />

      {/* Student Name */}
      <Box
        sx={{
          fontSize: "11px",
          fontWeight: 600,
          color: "#1F2937",
          textAlign: "center",
          wordBreak: "break-word",
          lineHeight: 1.3,
          width: "100%",
          flexGrow: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {studentName}
      </Box>
    </Box>
  );
};


const RollNumberManagement: React.FC = () => {
  const { t } = useTranslation();
  const { showError, showSuccess } = useError();
  const { user } = useAuth();
  const { selectedSession: contextSession, selectedClass: contextClass } = useSessionClass();
  const [rollNumberModalOpen, setRollNumberModalOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  
  const printRef = useRef<HTMLDivElement>(null);

  // Get schoolId
  const schoolId = user?.schoolId;

  const { updateRollNumberAndScholarNumberMutation, useStudentsBySessionIdAndClassId } = useStudents(false, schoolId || undefined);

  // Normalize IDs for student hook
  const selectedSessionId = contextSession?.id ?? null;
  const selectedClassId = contextClass?.id ?? null;
  
  const className = contextClass
    ? `${contextClass.name} ${contextClass.section || ""}`.trim()
    : "";
  const sessionYear = contextSession?.name ?? "";

  const { data: filteredStudentsFromAPI = [], isLoading: isLoadingFilteredStudents } = useStudentsBySessionIdAndClassId(
    selectedSessionId ?? undefined,
    selectedClassId ?? undefined
  );

  // Filter and sort active students
  const filteredStudents = useMemo(() => {
    if (!selectedSessionId || !selectedClassId) {
      return [];
    }
    return (filteredStudentsFromAPI || [])
      .filter((student) => student.status !== false)
      .sort((a, b) => {
        const nameA = a.studentName || "";
        const nameB = b.studentName || "";
        return nameA.localeCompare(nameB);
      });
  }, [selectedSessionId, selectedClassId, filteredStudentsFromAPI]);

  const handleOpenRollNumberModal = useCallback(() => {
    setRollNumberModalOpen(true);
  }, []);

  const handleCloseRollNumberModal = useCallback(() => {
    setRollNumberModalOpen(false);
  }, []);

  const handleOpenPreview = useCallback(() => {
    setPreviewOpen(true);
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewOpen(false);
  }, []);

  const downloadPdf = useCallback(async () => {
    const printArea = document.getElementById('roll-print-area');
    if (!printArea) return;
    
    setIsDownloadingPdf(true);
    
    try {
      // Temporarily make the print area fully visible and unclipped
      const originalStyle = printArea.style.cssText;
      printArea.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 794px;
        height: auto;
        overflow: visible;
        z-index: -9999;
        background: white;
      `;

      await new Promise(resolve => setTimeout(resolve, 300)); // wait for render

      const canvas = await html2canvas(printArea, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        scrollX: 0,
        scrollY: 0,
        windowWidth: 794,
        height: printArea.scrollHeight,  // ← full height, not visible height
        width: printArea.scrollWidth,
      } as any);

      printArea.style.cssText = originalStyle; // restore original style

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = 210;
      const pdfHeight = 297;
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;

      let position = 0;  // starts at 0 — top of image

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);

      let heightLeft = imgHeight - pdfHeight;

      while (heightLeft > 0) {
        position = position - pdfHeight;  // move up by one page
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      const cleanClassName = className.replace(/[^a-zA-Z0-9-_]/g, '_');
      const cleanSession = sessionYear.replace(/[^a-zA-Z0-9-_]/g, '_');
      pdf.save(`RollNumbers_Class_${cleanClassName}_${cleanSession}.pdf`);
      showSuccess(t("pdf_downloaded_successfully") || "PDF downloaded successfully");
    } catch (error) {
      showError(t("failed_download_pdf") || "Failed to download PDF");
    } finally {
      setIsDownloadingPdf(false);
    }
  }, [className, sessionYear, showSuccess, showError, t]);

  const handleSubmitRollNumbers = useCallback(
    async (students: StudentRollScholarUpdateDto[]) => {
      try {
        await updateRollNumberAndScholarNumberMutation.mutateAsync(students);
        showSuccess(
          t("roll_numbers_updated_successfully") || "Roll numbers updated successfully"
        );
        handleCloseRollNumberModal();
      } catch (error: any) {
        showError(
          error?.response?.data?.message ||
            t("error_updating_roll_numbers") ||
            "Error updating roll numbers"
        );
      }
    },
    [updateRollNumberAndScholarNumberMutation, handleCloseRollNumberModal, showError, showSuccess, t]
  );



  return (
    <>
      <Paper sx={{ p: 1.5, maxWidth: 1500, mx: "auto", mt: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1.25rem", whiteSpace: "nowrap" }}>
            {t("roll_number_management") || "Roll Number Management"}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<DownloadIcon />}
              disabled={filteredStudents.length === 0}
              onClick={handleOpenPreview}
              sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" }, height: 36 }}
            >
              {t("download_roll_numbers") || "Get Roll Numbers"}
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" }, height: 36 }}
              onClick={handleOpenRollNumberModal}
            >
              {t("add_roll_number") || "Add Roll Number"}
            </Button>
          </Box>
        </Box>
      </Paper>

      <RollNumberModal
        open={rollNumberModalOpen}
        handleClose={handleCloseRollNumberModal}
        onSubmit={handleSubmitRollNumbers}
      />

      {/* Centered Preview Dialog */}
      <CommonModal
        open={previewOpen}
        onClose={handleClosePreview}
        title={
          <Box>
            <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#fff" }}>
              {t("roll_number_cards_preview") || "Roll Number Cards Preview"}
            </Typography>
            <Typography sx={{ fontSize: "12px", color: "rgba(255,255,255,0.8)" }}>
              {`Class ${className} · Session ${sessionYear} · Total ${filteredStudents.length} Students`}
            </Typography>
          </Box>
        }
        maxWidth={false}
        fullWidth={false}
        PaperProps={{
          sx: {
            minWidth: "780px",
            maxWidth: "92vw",
            maxHeight: "85vh",
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
          }
        }}
        contentSx={{
          p: 3,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          bgcolor: "#f8fafc",
        }}
        customActions={
          <Button
            variant="contained"
            onClick={downloadPdf}
            disabled={isDownloadingPdf}
            startIcon={<DownloadIcon sx={{ fontSize: 18 }} />}
            sx={{
              background: "#1F2937",
              color: "#ffffff",
              padding: "9px 28px",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "600",
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { background: "#374151", boxShadow: "none" },
              "&.Mui-disabled": { background: "#9CA3AF", color: "#E5E7EB" }
            }}
          >
            {isDownloadingPdf ? `${t("downloading") || "Downloading"}...` : t("download") || "Download"}
          </Button>
        }
      >
        {/* Card Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "12px",
            width: "100%",
            justifyItems: "center",
            padding: "8px 16px",
            boxSizing: "border-box"
          }}
        >
          {filteredStudents.map((student) => (
            <RollNumberCard
              key={student.id}
              studentName={student.studentName}
              rollNumber={student.rollNumber}
              isPrint={false}
            />
          ))}
        </div>
      </CommonModal>

      {/* Hidden Off-Screen Container for Print Layout */}
      <div style={{ position: "absolute", left: "-9999px", top: "-9999px" }}>
        <div id="roll-print-area" ref={printRef} style={{ width: "790px", backgroundColor: "#ffffff", padding: "24px 30px" }}>
          <div 
            style={{ 
              display: "grid", 
              gridTemplateColumns: "repeat(3, 1fr)", 
              gap: "12px", 
              width: "100%",
            }}
          >
            {filteredStudents.map((student) => (
              <div 
                key={student.id} 
                style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "center", 
                  width: "100%" 
                }}
              >
                <RollNumberCard
                  studentName={student.studentName}
                  rollNumber={student.rollNumber}
                  isPrint={true}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Inject Style Overrides for Media Print */}
      <style>
        {`
          @media print {
            body * { visibility: hidden; }
            #roll-print-area, #roll-print-area * { visibility: visible; }
            #roll-print-area {
              position: absolute; top: 0; left: 0;
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 8px;
              padding: 10mm;
              width: 190mm;
            }
            .roll-card-print {
              border: 1px solid #ccc;
              border-radius: 6px;
              padding: 6px;
              text-align: center;
              page-break-inside: avoid;
            }
          }
        `}
      </style>
    </>
  );
};

export default RollNumberManagement;
